'use client';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { contentfulClient } from '../';
import type {
  ReleaseNote,
  ReleaseNoteCategory,
  ReleaseNoteSkeleton,
} from '../types';
import { transformReleaseNoteEntry } from '../utils';

interface ReleaseNotesByCategoryOptions {
  readonly category: ReleaseNoteCategory;
  readonly limit?: number;
  readonly skip?: number;
}

/**
 * Fetches release notes filtered by category from Contentful
 *
 * @param options - Options including category filter and pagination
 * @returns Array of release notes matching the category
 */
async function fetchReleaseNotesByCategory(
  options: ReleaseNotesByCategoryOptions
): Promise<ReleaseNote[]> {
  // Return empty array if Contentful is not configured
  if (!contentfulClient) {
    console.warn(
      'Contentful client is not configured. Returning empty release notes.'
    );
    return [];
  }

  const { category, limit = 10, skip = 0 } = options;

  const query: Record<string, unknown> = {
    content_type: 'releaseNote',
    'fields.categories[in]': category,
    limit,
    skip,
    order: '-fields.date',
  };

  try {
    const response =
      await contentfulClient.getEntries<ReleaseNoteSkeleton>(query);

    // Transform entries and filter out any that fail validation
    const notes: ReleaseNote[] = [];

    for (const entry of response.items) {
      try {
        notes.push(transformReleaseNoteEntry(entry));
      } catch (error) {
        console.error('Failed to transform release note entry:', error);
        // Skip this entry and continue with others
      }
    }

    return notes;
  } catch (error) {
    console.error(
      'Failed to fetch release notes by category from Contentful:',
      error
    );
    return [];
  }
}

/**
 * Hook to fetch release notes filtered by category from Contentful
 *
 * This hook is useful for displaying release notes grouped by type
 * (e.g., showing only new features or bug fixes).
 *
 * @param options - Options including category filter and pagination
 * @param reactQueryOptions - Additional React Query options
 * @returns UseQueryResult containing release notes for the specified category
 *
 * @example
 * ```tsx
 * // Get all new features
 * const { data: newFeatures } = useGetReleaseNotesByCategory({
 *   category: 'features',
 *   limit: 10
 * });
 *
 * // Get bug fixes
 * const { data: bugFixes } = useGetReleaseNotesByCategory({
 *   category: 'bugfixes'
 * });
 * ```
 */
export function useGetReleaseNotesByCategory(
  options: ReleaseNotesByCategoryOptions,
  reactQueryOptions?: Omit<
    UseQueryOptions<ReleaseNote[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<ReleaseNote[], Error>({
    queryKey: ['contentful', 'release-notes-by-category', options],
    queryFn: () => fetchReleaseNotesByCategory(options),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    ...reactQueryOptions,
  });
}
